import React, { Component } from 'react'

import { connect } from 'react-redux'

import * as actions from '../actions'
import { withFirebase } from '../Firebase'
import * as ContentLayout from './ContentLayout'
import * as Content from '../reducers/Content'
import TextNode from '../nodes/TextNode'
import FolderNode from '../nodes/FolderNode'
import ImageNode from '../nodes/ImageNode'
import VideoNode from '../nodes/VideoNode'
import NodeContainer from '../nodes/NodeContainer'
import { uuidImageName } from '../helpers'

const NewNodeButton = (props) => (
  <div className="newNodeButton" {...props.divProps}>
    <div className="newNodeButtonText">{props.text}</div>
  </div>
)

function nodeComponent(node) {
  switch (Content.nodeType(node)) {
    case 'folder':
      return FolderNode
    case 'image':
      return ImageNode
    case 'video':
      return VideoNode
    default:
      return TextNode
  }
}

const addKeyId = 'addKey'
const addFolderId = 'addFolder'

const insertNodes = [{ id: addKeyId }, { id: addFolderId }]

const Nodes = (props) => {
  const {
    isViewOnly,
    layout,
    dragNode,
    onClick,
    onDropImage,
    onDragStart,
    onDrag,
    onDragEnd,
  } = props

  const isDragging = dragNode !== null

  const style = {
    height: `${layout.height + 1}px`, // we add one extra pixel here to avoid last node not fitting in due to rounding issues
  }

  return (
    <div
      className={`folderContentNodes${isDragging ? ' isDragging' : ''}`}
      style={style}
    >
      {layout.tiles.map((tile) => {
        const { node, rect } = tile

        const position = {
          left: `${rect.x}px`,
          top: `${rect.y}px`,
        }

        if (node.id === addKeyId) {
          return isViewOnly ? null : (
            <NewNodeButton
              text="New Key"
              key={node.id}
              divProps={{
                style: position,
                onClick: () => onClick(node),
              }}
            />
          )
        }

        if (node.id === addFolderId) {
          return isViewOnly ? null : (
            <NewNodeButton
              text="New Folder"
              key={node.id}
              divProps={{
                style: position,
                onClick: () => onClick(node),
              }}
            />
          )
        }

        const beingDragged = node === dragNode

        const Node = nodeComponent(node)

        return (
          <NodeContainer
            rowCount={rect.rowCount}
            key={node.id}
            onDropImage={
              Node === ImageNode
                ? (image) => onDropImage(node, image)
                : undefined
            }
            divProps={{
              style: position,
              draggable: !isViewOnly,
              beingdragged: beingDragged ? 'placeholder' : 'no',
              onClick: () => onClick(node),
              onDragStart: (event) => onDragStart(node, event),
              onDrag: (event) => onDrag(node, event),
              onDragEnd: (event) => onDragEnd(node, event),
            }}
          >
            <Node node={node} />
          </NodeContainer>
        )
      })}
    </div>
  )
}

function array_move(arr, old_index, new_index) {
  const copy = [...arr]
  copy.splice(new_index, 0, copy.splice(old_index, 1)[0])
  return copy
}

class FolderContent extends Component {
  constructor(props) {
    super(props)
    this.state = {
      dragStartLocation: {},
      dragStartIndex: -1,
      dragLayout: null,
      dragNode: null,
      dropFrames: [],
      dropIndex: -1,
      dropLocation: {},
    }
  }

  setDragStart = (startLocation, startIndex, nodeStartLocation, node) => {
    const dropFrames = node
      ? ContentLayout.dropFrames(this.props.folder.content, node)
      : []

    this.setState({
      dragStartLocation: startLocation,
      dragStartIndex: startIndex,
      dragNode: node,
      dragLayout: null,
      dragNodeStartLocation: nodeStartLocation,
      dropFrames,
      dropIndex: startIndex,
    })
  }

  setDropLocation = (dropIndex, dropLocation) => {
    if (
      this.state.dropIndex !== dropIndex ||
      this.state.dropLocation !== dropLocation
    ) {
      const { dragStartIndex } = this.state

      let layout

      if (dropIndex >= 0) {
        if (dropIndex !== this.state.dropIndex) {
          let { content } = this.props.folder

          if (dragStartIndex >= 0 && dropIndex >= 0) {
            content = array_move(content, dragStartIndex, dropIndex)
          }

          content = content.concat(insertNodes)

          layout = ContentLayout.layout(content)

          if (dragStartIndex >= 0 && dropIndex >= 0) {
            const dragTile = layout.tiles.splice(dropIndex, 1)[0]
            // keep dragged node layout at end of array for animation to work correctly
            layout.tiles.splice(layout.tiles.length - 3, 0, dragTile)
          }
        } else {
          layout = this.state.layout
        }
      } else {
        layout = null
      }

      if (layout !== this.state.layout) {
        this.setState({
          dragLayout: layout,
          dropIndex,
          dropLocation,
        })
      } else {
        this.setState({
          dropIndex,
          dropLocation,
        })
      }
    }
  }

  addFolder = () => {
    const { addAndSelectFolder, folder } = this.props
    addAndSelectFolder(folder.id)
  }

  addTextNode = () => {
    const { addAndSelectTextNode, folder } = this.props
    addAndSelectTextNode(folder.id)
  }

  onNodeClick = (node) => {
    if (node.id === addKeyId) {
      this.addTextNode()
      return
    }
    if (node.id === addFolderId) {
      this.addFolder()
      return
    }
    const { contentId, selectNodeId } = this.props
    selectNodeId(node.id, contentId)
  }

  onNodeDropImage = (node, image) => {
    const { firebase, setUploadingImage, removeUploading } = this.props
    const imageName = uuidImageName(image)
    setUploadingImage(node.id, imageName)

    firebase
      .putImage(image, imageName)
      .then(function (snapshot) {
        removeUploading(imageName)
      })
      .catch(function (error) {
        removeUploading(imageName)
      })
  }

  hitIndex(location) {
    const { x } = location
    const { y } = location

    let hitIndex = -1
    let min = 100000000

    const { columnSpacing } = ContentLayout.metric

    this.state.dropFrames.forEach((frame, i) => {
      const { rect } = frame
      if (
        (x <= rect.x + rect.width + columnSpacing / 2 && rect.column === 0) ||
        (x >= rect.x - columnSpacing / 2 && rect.column === 1)
      ) {
        const dy = Math.abs(y - (rect.y + rect.height / 2))
        if (dy < min) {
          min = dy
          hitIndex = frame.index
        }
      }
    })

    return hitIndex
  }

  updateDropLocation(eventLocation) {
    const { dragStartLocation } = this.state

    const dropLocation = {
      x: eventLocation.x - dragStartLocation.x,
      y: eventLocation.y - dragStartLocation.y,
    }

    this.setDropLocation(this.hitIndex(dropLocation), dropLocation)
  }

  onNodeDragStart = (node, event) => {
    const { content } = this.props.folder
    const location = {
      x: event.clientX,
      y: event.clientY,
    }

    const nodeIndex = content.indexOf(node)
    const layout = ContentLayout.layout(content)
    const nodeRect = layout.tiles[nodeIndex].rect

    const img = document.createElement('img')
    img.src =
      'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
    event.dataTransfer.setDragImage(img, 0, 0)

    event.dataTransfer.dropEffect = 'move'

    this.setDragStart(
      location,
      nodeIndex,
      { x: nodeRect.x, y: nodeRect.y },
      node,
    )
  }

  onNodeDrag = (node, event) => {
    const { clientX } = event
    const { clientY } = event

    if (clientX !== 0 && clientY !== 0) {
      const dropLocation = {
        x: clientX - this.state.dragStartLocation.x,
        y: clientY - this.state.dragStartLocation.y,
      }
      this.setDropLocation(this.hitIndex(dropLocation), dropLocation)
    }
  }

  onNodeDragEnd = (node, event) => {
    this.props.moveNode(this.state.dragNode.id, this.state.dropIndex)
    this.setDragStart({}, -1, {}, null)
    this.setDropLocation(-1, {})
  }

  render() {
    const { dragNode, dragNodeStartLocation, dropLocation } = this.state
    const { isViewOnly } = this.props

    const layout =
      this.state.dragLayout !== null
        ? this.state.dragLayout
        : ContentLayout.layout(this.props.folder.content.concat(insertNodes))

    let dragNodeStyle = {}
    let dragNodeRowCount = 1

    if (dragNode) {
      const nodeTile = layout.tiles.find((tile) => tile.node === dragNode)
      if (nodeTile) {
        const dragLocation = { x: nodeTile.rect.x, y: nodeTile.rect.y }
        if (dropLocation.x !== undefined) {
          dragLocation.x = dragNodeStartLocation.x + dropLocation.x
          dragLocation.y = dragNodeStartLocation.y + dropLocation.y
        }
        dragNodeStyle = {
          left: `${dragLocation.x}px`,
          top: `${dragLocation.y}px`,
        }
        dragNodeRowCount = nodeTile.rect.rowCount
      }
    }

    const Node = dragNode ? nodeComponent(dragNode) : undefined

    return (
      <div className="folderContent">
        <div className="folderContentDraggedNodeContainer">
          {dragNode && (
            <NodeContainer
              key={dragNode.id}
              rowCount={dragNodeRowCount}
              divProps={{
                style: dragNodeStyle,
                beingdragged: 'yes',
              }}
            >
              <Node node={dragNode} />
            </NodeContainer>
          )}
        </div>
        <div className="folderContentNodesContainer">
          <Nodes
            isViewOnly={isViewOnly}
            layout={layout}
            dragNode={dragNode}
            onClick={this.onNodeClick}
            onDropImage={this.onNodeDropImage}
            onDragStart={this.onNodeDragStart}
            onDrag={this.onNodeDrag}
            onDragEnd={this.onNodeDragEnd}
          />
        </div>
      </div>
    )
  }
}

function mapFolderStateToProps(state) {
  return {}
}

const ConnectedFolderContent = connect(
  mapFolderStateToProps,
  actions,
)(withFirebase(FolderContent))
export default ConnectedFolderContent
